import type { DateValue } from "@internationalized/date";
import type { DateRange } from "reka-ui";
import type { ComponentPropsOptions } from "vue";
import type { Shortcuts } from "./shortcuts";
import type { Size } from "@/components/helpers/common-props";
import { CalendarDateTime, Time } from "@internationalized/date";
import { mapKeys } from "lodash-es";
import { computed, defineComponent, shallowRef, watch } from "vue";
import Calendar from "@/components/primitives/calendar/Calendar";
import RangeCalendar from "@/components/primitives/calendar/RangeCalendar";
import DateField from "@/components/primitives/date-field/DateField";
import TimeField from "@/components/primitives/time-field/TimeField";
import { $t, locale } from "@/services/i18n";
import { calendarDayCount } from "@/utils/calendar-date";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";
import { dateValueToTimestamp, toCalendarDateTimeValue, toCalendarDateValue } from "./date-utils";
import { getPastShortcuts } from "./shortcuts";

export type DatePickerType = "date" | "daterange" | "datetime" | "datetimerange";

export const datetimeProps = {
  modelValue: p.custom<number | string | Date | number[] | null>([Number, Array, Date, String]),
  type: p.string<DatePickerType>("datetime"),
  shortcuts: p.object<Shortcuts>(getPastShortcuts()),
  disabled: p.boolean(),
  size: p.string<Size>("md"),
  clearable: p.boolean(true),
  isDateDisabled: p.func<(timestamp: number) => boolean>(),
  maxValue: p.custom<number | Date | null>([Number, Date]),
  minValue: p.custom<number | Date | null>([Number, Date]),
  maximumDays: p.number(),
} satisfies ComponentPropsOptions;

export default defineComponent({
  name: "DateTimePicker",
  emits: ["update:modelValue", "blur", "clear", "confirm", "change"],
  props: datetimeProps,
  setup(props, { emit }) {
    const initialType = typeof props.modelValue;
    const isRangeType = computed(() => ["daterange", "datetimerange"].includes(props.type));
    const hasTime = computed(() => ["datetime", "datetimerange"].includes(props.type));
    const calendarLocale = computed(() => (locale.value === "pt" ? "pt-BR" : "en-US"));

    // Bridge isDateDisabled from (timestamp) => boolean to Reka UI Matcher (DateValue => boolean)
    const rekaIsDateDisabled = computed(() => {
      if (!props.isDateDisabled) return undefined;
      return (dateValue: DateValue) => {
        const timestamp = dateValueToTimestamp(dateValue);
        return props.isDateDisabled!(timestamp);
      };
    });

    // Convert maxValue/minValue from timestamps to DateValue
    const rekaMaxValue = computed(() => {
      if (props.maxValue == null) return undefined;
      return toCalendarDateValue(
        props.maxValue instanceof Date ? props.maxValue.getTime() : props.maxValue
      );
    });
    const rekaMinValue = computed(() => {
      if (props.minValue == null) return undefined;
      return toCalendarDateValue(
        props.minValue instanceof Date ? props.minValue.getTime() : props.minValue
      );
    });

    // Clamp a DateValue within min/max bounds
    function clampDate(val: DateValue): DateValue {
      if (rekaMaxValue.value && val.compare(rekaMaxValue.value) > 0) return rekaMaxValue.value;
      if (rekaMinValue.value && val.compare(rekaMinValue.value) < 0) return rekaMinValue.value;
      return val;
    }

    // Clamp range start ensuring maximumDays constraint; adjusts end if needed
    function clampRangeStart(newStart: DateValue, currentEnd: DateValue): DateRange {
      const start = clampDate(newStart);
      if (!props.maximumDays) return { start, end: currentEnd } as DateRange;
      if (calendarDayCount(start, currentEnd) > props.maximumDays) {
        const end = clampDate(start.add({ days: props.maximumDays - 1 }));
        return { start, end } as DateRange;
      }
      return { start, end: currentEnd } as DateRange;
    }

    // Clamp range end ensuring maximumDays constraint; adjusts start if needed
    function clampRangeEnd(currentStart: DateValue, newEnd: DateValue): DateRange {
      const end = clampDate(newEnd);
      if (!props.maximumDays) return { start: currentStart, end } as DateRange;
      if (calendarDayCount(currentStart, end) > props.maximumDays) {
        const start = clampDate(end.subtract({ days: props.maximumDays - 1 }));
        return { start, end } as DateRange;
      }
      return { start: currentStart, end } as DateRange;
    }

    // --- Time models for datetime/datetimerange ---
    // Using shallowRef to avoid deep reactivity issues with @internationalized/date private fields
    const startTime = shallowRef<Time | undefined>(undefined);
    const endTime = shallowRef<Time | undefined>(undefined);

    // --- Single date model (type: "date" | "datetime") ---
    const singleModel = computed({
      get(): DateValue | undefined {
        if (props.modelValue == null) return undefined;
        const val = Array.isArray(props.modelValue) ? props.modelValue[0] : props.modelValue;
        if (val == null) return undefined;
        return hasTime.value ? toCalendarDateTimeValue(val) : toCalendarDateValue(val);
      },
      set(dateValue: DateValue | undefined) {
        if (!dateValue) {
          emit("update:modelValue", null);
          return;
        }
        // Merge with time if datetime type
        let finalValue = dateValue;
        if (hasTime.value && startTime.value && !(dateValue instanceof CalendarDateTime)) {
          finalValue = new CalendarDateTime(
            dateValue.year,
            dateValue.month,
            dateValue.day,
            startTime.value.hour,
            startTime.value.minute
          );
        }
        emitValue(dateValueToTimestamp(finalValue));
      },
    });

    // --- Range date model (type: "daterange" | "datetimerange") ---
    const rangeModel = computed({
      get(): DateRange | undefined {
        if (!Array.isArray(props.modelValue) || props.modelValue.length < 2) return undefined;
        const [start, end] = props.modelValue;
        if (start == null || end == null) return undefined;
        return {
          start: hasTime.value ? toCalendarDateTimeValue(start) : toCalendarDateValue(start),
          end: hasTime.value ? toCalendarDateTimeValue(end) : toCalendarDateValue(end),
        };
      },
      set(range: DateRange | undefined) {
        if (!range?.start || !range?.end) {
          emit("update:modelValue", null);
          return;
        }

        let startTs = dateValueToTimestamp(range.start);
        let endTs = dateValueToTimestamp(range.end);

        // For daterange (no time picker), set end date to end-of-day.
        // If end is today, cap at 23:59:59; if in the past, set 23:59:59.
        if (props.type === "daterange") {
          const endDate = new Date(endTs);
          endDate.setHours(23, 59, 59, 999);

          // If end is today, cap at current time instead of 23:59:59
          const today = new Date();
          if (
            endDate.getFullYear() === today.getFullYear() &&
            endDate.getMonth() === today.getMonth() &&
            endDate.getDate() === today.getDate()
          ) {
            endDate.setHours(today.getHours(), today.getMinutes(), today.getSeconds(), today.getMilliseconds());
          }

          endTs = endDate.getTime();
        }

        emitValue([startTs, endTs]);
      },
    });

    // Initialize time refs from model value
    watch(
      () => props.modelValue,
      (val) => {
        if (!hasTime.value) return;

        if (Array.isArray(val) && val.length >= 2) {
          if (val[0] != null) {
            const dt = toCalendarDateTimeValue(val[0]);
            startTime.value = new Time(dt.hour, dt.minute, dt.second);
          }
          if (val[1] != null) {
            const dt = toCalendarDateTimeValue(val[1]);
            endTime.value = new Time(dt.hour, dt.minute, dt.second);
          }
        } else if (val != null && !Array.isArray(val)) {
          const dt = toCalendarDateTimeValue(val);
          startTime.value = new Time(dt.hour, dt.minute, dt.second);
        }
      },
      { immediate: true }
    );

    // Detect if the initial model value was an array of Date objects
    const initialIsDateArray =
      Array.isArray(props.modelValue) &&
      props.modelValue.length > 0 &&
      (props.modelValue[0] as unknown) instanceof Date;

    // Emit value in the original format
    function emitValue(val: number | number[]) {
      if (Array.isArray(val) && initialIsDateArray) {
        emit(
          "update:modelValue",
          val.map((ts) => new Date(ts))
        );
      } else if (initialType === "object" && typeof val === "number") {
        emit("update:modelValue", new Date(val));
      } else if (initialType === "string" && typeof val === "number") {
        emit("update:modelValue", new Date(val).toISOString());
      } else {
        emit("update:modelValue", val);
      }
      emit("change");
    }

    // Translate shortcut keys and filter out shortcuts that exceed maximumDays
    const translatedShortcuts = computed(() => {
      if (!props.shortcuts) return {};
      const filtered = props.maximumDays
        ? Object.fromEntries(
            Object.entries(props.shortcuts).filter(([, fn]) => {
              const [start, end] = fn();
              return calendarDayCount(
                toCalendarDateValue(start),
                toCalendarDateValue(end),
              ) <= props.maximumDays!;
            }),
          )
        : props.shortcuts;
      return mapKeys(filtered, (_value, key) => {
        // @ts-expect-error dynamic key
        const translated = $t(key);
        return translated === key ? key : translated;
      });
    });

    function applyShortcut(fn: () => [number, number]) {
      const [start, end] = fn();
      if (isRangeType.value) {
        emitValue([start, end]);
      }
      emit("confirm");
    }

    function handleClear() {
      emit("update:modelValue", null);
      emit("clear");
    }

    // Handle time changes for datetime types
    function handleStartTimeChange(time: Time) {
      startTime.value = time;
      if (!isRangeType.value && singleModel.value) {
        const date = singleModel.value;
        const newDateTime = new CalendarDateTime(
          date.year,
          date.month,
          date.day,
          time.hour,
          time.minute
        );
        emitValue(dateValueToTimestamp(newDateTime));
      } else if (isRangeType.value && rangeModel.value?.start && rangeModel.value?.end) {
        const start = rangeModel.value.start;
        const startDt = new CalendarDateTime(
          start.year,
          start.month,
          start.day,
          time.hour,
          time.minute
        );
        const endTs = dateValueToTimestamp(rangeModel.value.end);
        emitValue([dateValueToTimestamp(startDt), endTs]);
      }
    }

    function handleEndTimeChange(time: Time) {
      endTime.value = time;
      if (isRangeType.value && rangeModel.value?.start && rangeModel.value?.end) {
        const end = rangeModel.value.end;
        const endDt = new CalendarDateTime(end.year, end.month, end.day, time.hour, time.minute);
        const startTs = dateValueToTimestamp(rangeModel.value.start);
        emitValue([startTs, dateValueToTimestamp(endDt)]);
      }
    }

    const showFooter = computed(() => props.modelValue != null);

    return () => (
      <div class="flex">
        {/* Shortcuts sidebar for range types */}
        {isRangeType.value && props.shortcuts && Object.keys(props.shortcuts).length > 0 && (
          <div class="border-input flex flex-col gap-0.5 border-r px-3 py-3">
            {Object.entries(translatedShortcuts.value).map(([label, fn]) => (
              <button
                key={label}
                type="button"
                class={cn(
                  "text-muted-foreground rounded-md px-3 py-1.5 text-left text-sm whitespace-nowrap transition-colors",
                  "hover:bg-muted hover:text-foreground"
                )}
                onClick={() => applyShortcut(fn as () => [number, number])}
              >
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Calendar area */}
        <div class="flex flex-col">
          {/* Single date calendar */}
          {!isRangeType.value && (
            <Calendar.Root
              modelValue={singleModel.value}
              onUpdate:modelValue={(val: DateValue | undefined) => {
                singleModel.value = val;
                if (props.type === "date") emit("confirm");
              }}
              locale={calendarLocale.value}
              disabled={props.disabled}
              isDateDisabled={rekaIsDateDisabled.value}
              minValue={rekaMinValue.value}
              maxValue={rekaMaxValue.value}
            />
          )}

          {/* Range calendar */}
          {isRangeType.value && (
            <RangeCalendar.Root
              modelValue={rangeModel.value ?? undefined}
              onUpdate:modelValue={(val: DateRange) => {
                rangeModel.value = val;
                if (props.type === "daterange") emit("confirm");
              }}
              locale={calendarLocale.value}
              disabled={props.disabled}
              isDateDisabled={rekaIsDateDisabled.value}
              minValue={rekaMinValue.value}
              maxValue={rekaMaxValue.value}
              maximumDays={props.maximumDays}
              pagedNavigation
            />
          )}

          {/* Footer: date fields + time fields + clear button */}
          {showFooter.value && (
            <div class="border-input mx-3 flex items-center gap-2 border-t py-2">
              {/* Single date/datetime */}
              {!isRangeType.value && (
                <>
                  <DateField.Root
                    modelValue={singleModel.value}
                    onUpdate:modelValue={(val: DateValue) => {
                      singleModel.value = clampDate(val);
                    }}
                    locale={calendarLocale.value}
                    maxValue={rekaMaxValue.value}
                    minValue={rekaMinValue.value}
                    class="h-auto w-auto border-0 px-0 py-0"
                  />
                  {hasTime.value && (
                    <TimeField.Root
                      modelValue={startTime.value}
                      onUpdate:modelValue={(val) => {
                        if (val) handleStartTimeChange(val as Time);
                      }}
                      granularity="minute"
                      locale={calendarLocale.value}
                      class="h-auto w-auto border-0 px-0 py-0"
                    />
                  )}
                </>
              )}
              {/* Range daterange/datetimerange */}
              {isRangeType.value && rangeModel.value && (
                <>
                  <DateField.Root
                    aria-label={$t("dates.startDate")}
                    modelValue={rangeModel.value.start}
                    onUpdate:modelValue={(val: DateValue) => {
                      if (rangeModel.value?.end) {
                        rangeModel.value = clampRangeStart(val, rangeModel.value.end);
                      }
                    }}
                    locale={calendarLocale.value}
                    maxValue={rekaMaxValue.value}
                    minValue={rekaMinValue.value}
                    class="h-auto w-auto border-0 px-0 py-0"
                  />
                  {hasTime.value && (
                    <TimeField.Root
                      modelValue={startTime.value}
                      onUpdate:modelValue={(val) => {
                        if (val) handleStartTimeChange(val as Time);
                      }}
                      granularity="minute"
                      locale={calendarLocale.value}
                      class="h-auto w-auto border-0 px-0 py-0"
                    />
                  )}
                  <span class="text-muted-foreground">–</span>
                  <DateField.Root
                    aria-label={$t("dates.endDate")}
                    modelValue={rangeModel.value.end}
                    onUpdate:modelValue={(val: DateValue) => {
                      if (rangeModel.value?.start) {
                        rangeModel.value = clampRangeEnd(rangeModel.value.start, val);
                      }
                    }}
                    locale={calendarLocale.value}
                    maxValue={rekaMaxValue.value}
                    minValue={rekaMinValue.value}
                    class="h-auto w-auto border-0 px-0 py-0"
                  />
                  {hasTime.value && (
                    <TimeField.Root
                      modelValue={endTime.value}
                      onUpdate:modelValue={(val) => {
                        if (val) handleEndTimeChange(val as Time);
                      }}
                      granularity="minute"
                      locale={calendarLocale.value}
                      class="h-auto w-auto border-0 px-0 py-0"
                    />
                  )}
                </>
              )}
              {/* Clear button - right-aligned */}
              {props.clearable && props.modelValue != null && (
                <button
                  type="button"
                  class="text-muted-foreground hover:bg-muted hover:text-foreground ml-auto rounded-md px-2 py-1 text-xs transition-colors"
                  onClick={handleClear}
                >
                  {$t("clear") || "Clear"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
});
